import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ConfigurationSaveStatusDispatch, {
  useConfigurationSaveStatusContext,
} from "../../context/configurationSaveStatusContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { CustomError, isNotFound } from "../../http/errors";
import { Configuration as Conf } from "../../http/types/configurations";
import { useConfigurationsApi } from "../../http/useConfigurations";
import Button from "../ui/Button";
import ErrorText from "../ui/ErrorText";
import FlexContent from "../ui/FlexContent";
import Header, { HeaderPath } from "../ui/Header";
import ModalDialog from "../ui/ModalDialog";
import StatusBadge from "../ui/StatusBadge";
import AddConfigurationToProjectDialog from "./AddConfigurationToProjectDialog";
import ConfigurationSharingAccessesDialog from "./ConfigurationSharingAccessesDialog";
import ProjectInfosDialog from "./ProjectInfosDialog";
import ProjectMenuButton from "./ProjectMenuButton";
import Border from "./border/Border";
// import Ladstep from "./ladstep/Ladstep";
// import Overpass from "./overpass/Overpass";
import Patrol from "./new-patrol/Patrol";
// import SafetyNet from "./safetyNet/SafetyNet";
// import { isGuard, isLadstep, isOverpass, isPatrol, isSafetyNet, isVertigrip } from "./typeGurads";
import HRail from "./hrail/HRail";
import { isBorder, isHRail, isPatrol, isVertigrip } from "./typeGurads";
import Vertigrip from "./vertigrip/Vertigrip";

interface ConfigurationProps {
  isAdmin?: boolean;
}

export interface UpdateConfigurationFormBody {
  name: string;
  description?: string;
}

const Configuration: FC<ConfigurationProps> = ({ isAdmin }: ConfigurationProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSharingDialogOpen, setSharingDialogOpen] = useState(false);
  const [isAddToProjectDialogOpen, setAddToProjectDialogOpen] = useState(false);
  const [isProjectInfosDialogOpen, setProjectInfosDialogOpen] = useState(false);
  const [isUnlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [saveStatus, setSaveStatus] = useConfigurationSaveStatusContext();
  const { projectId } = useParams<{ projectId: string }>();
  const { configurationId } = useParams<{ configurationId: string }>();
  const configurationsApi = useConfigurationsApi();

  const aborter = useRef(new AbortController());
  const { error, data } = configurationsApi.getConfiguration(configurationId!, aborter.current.signal);

  // Store variables in order to avoid the UI to rerender when mutating
  const [state, setState] = useState<{ data?: Conf; error?: CustomError }>();

  useEffect(() => {
    setState({
      data: data ?? state?.data,
      error: error ?? state?.error,
    });
  }, [data, error]);

  const path: HeaderPath[] = projectId
    ? [
        {
          url: `${isAdmin ? "/admin" : ""}/projects`,
          display: i18n.translation.projects.my,
        },
        {
          url: `${isAdmin ? "/admin" : ""}/projects/${state?.data?.project?.id}?tab=1`,
          display: `${state?.data?.project?.name ?? ""}: ${i18n.translation.configurations.plural.toLowerCase()}`,
        },
      ]
    : [
        {
          url: `${isAdmin ? "/admin" : ""}/configurations`,
          display: isAdmin ? i18n.translation.configurations.all : i18n.translation.configurations.my,
        },
      ];

  path.push({
    display: state?.data?.name ?? "",
  });

  if (!state?.data && !state?.error) {
    return;
  }

  return !state?.error ? (
    <ConfigurationSaveStatusDispatch.Provider value={setSaveStatus}>
      <Header
        title={state.data?.system?.name ?? i18n.translation.common.loading}
        path={path}
        buttons={[
          state.data?.canEdit && (
            <StatusBadge color={saveStatus === "saved" ? "success" : "warning"} dot>
              {saveStatus === "saved" ? i18n.translation.common.saved : i18n.translation.common.draft}
            </StatusBadge>
          ),
          !state.data?.project && state.data?.canEdit && (
            <Button buttonProps={{ onClick: () => setAddToProjectDialogOpen(true) }}>
              {i18n.translation.configurations.addToProject}
            </Button>
          ),
          !!state.data?.project && (state.data.project.shared || state.data.project.canEdit) && (
            <ProjectMenuButton
              onDetails={() => setProjectInfosDialogOpen(true)}
              onUnlink={state.data?.canEdit ? () => setUnlinkConfirmOpen(true) : undefined}
            />
          ),
        ]}
      />
      {isVertigrip(state.data) ? (
        <Vertigrip configuration={state.data} isAdmin={isAdmin}></Vertigrip>
      ) : isBorder(state.data) ? (
        <Border configuration={state.data} isAdmin={isAdmin}></Border>
      ) : isPatrol(state.data) ? (
        <Patrol configuration={state.data} isAdmin={isAdmin}></Patrol>
      ) : isHRail(state.data) ? (
        <HRail configuration={state.data} isAdmin={isAdmin}></HRail>
      ) : // ) : isSafetyNet(state.data) ? (
      //   <SafetyNet {...props}></SafetyNet>
      // ) : isLadstep(state.data) ? (
      //   <Ladstep {...props}></Ladstep>
      // ) : isOverpass(state.data) ? (
      //   <Overpass {...props}></Overpass>
      undefined}
      {isSharingDialogOpen && state.data?.id && (
        <ConfigurationSharingAccessesDialog
          configurationId={state.data.id}
          configurationName={state.data.name}
          isAdmin={isAdmin}
          onClose={() => setSharingDialogOpen(false)}
        />
      )}
      {isAddToProjectDialogOpen && state.data?.id && (
        <AddConfigurationToProjectDialog
          configurationId={state.data.id}
          onClose={() => setAddToProjectDialogOpen(false)}
          onSave={() => setAddToProjectDialogOpen(false)}
        />
      )}
      {isProjectInfosDialogOpen && state.data?.project?.id && (
        <ProjectInfosDialog
          projectId={state.data.project.id}
          isAdmin={isAdmin}
          onClose={() => setProjectInfosDialogOpen(false)}
        />
      )}
      {isUnlinkConfirmOpen && state.data?.id && state.data?.project?.id && (
        <ModalDialog
          onClose={() => setUnlinkConfirmOpen(false)}
          title={i18n.translation.configurations.unlinkFromProject}
          isSubmitting={isUnlinking}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setUnlinkConfirmOpen(false),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsUnlinking(true);
                configurationsApi
                  .removeConfigurationFromProject(state.data!.id, state.data!.project!.id)
                  .then(() => setUnlinkConfirmOpen(false))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsUnlinking(false));
              },
            },
          ]}
        >
          {i18n.translation.configurations.questions.unlinkFromProject}
        </ModalDialog>
      )}
    </ConfigurationSaveStatusDispatch.Provider>
  ) : (
    <>
      <Header
        title={isNotFound(error) ? i18n.translation.common.notFound : i18n.translation.common.error}
        error={error}
      />
      <FlexContent padding>
        <ErrorText
          text={isNotFound(error) ? i18n.translation.common.resourceNotFound : i18n.translation.common.genericError}
        />
      </FlexContent>
    </>
  );
};

export default Configuration;
